<template>
  <Card class="max-w-2xl">
    <template #header>
      <div>
        <h2 class="text-lg font-semibold text-gray-900 dark:text-white">Change Password</h2>
        <p class="text-sm text-gray-600 dark:text-gray-300 mt-1">
          Update your password to keep your account secure
        </p>
      </div>
    </template>

    <!-- Success Message -->
    <Alert v-if="updateSuccess" type="success" class="mb-6" @dismiss="clearMessages">
      Password changed successfully!
    </Alert>

    <!-- Error Message -->
    <Alert v-if="hasError" type="error" class="mb-6" @dismiss="clearMessages">
      {{ error }}
    </Alert>

    <!-- Password Change Form -->
    <form @submit.prevent="handleSubmit" class="space-y-6">
      <!-- Current Password -->
      <div>
        <label
          for="currentPassword"
          class="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2"
        >
          Current Password
        </label>
        <Input
          id="currentPassword"
          v-model="form.currentPassword"
          type="password"
          placeholder="Enter your current password"
          :error="currentPasswordError"
          :helper-text="currentPasswordError"
          :disabled="isLoading"
          required
          class="w-full"
        />
      </div>

      <!-- New Password -->
      <div>
        <label
          for="newPassword"
          class="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2"
        >
          New Password
        </label>
        <Input
          id="newPassword"
          v-model="form.newPassword"
          type="password"
          placeholder="Enter your new password"
          :error="newPasswordError"
          :helper-text="newPasswordError"
          :disabled="isLoading"
          required
          class="w-full"
        />

        <!-- Password Strength Indicator -->
        <div v-if="form.newPassword" class="mt-2">
          <div class="text-xs text-gray-600 dark:text-gray-400 mb-1">Password strength:</div>
          <div class="flex space-x-1">
            <div
              v-for="i in 4"
              :key="i"
              :class="[
                'h-1 flex-1 rounded-full',
                i <= passwordStrength ? strengthColor : 'bg-gray-200 dark:bg-gray-600',
              ]"
            ></div>
          </div>
          <div class="text-xs mt-1" :class="strengthTextColor">
            {{ strengthText }}
          </div>
        </div>
      </div>

      <!-- Confirm New Password -->
      <div>
        <label
          for="confirmPassword"
          class="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2"
        >
          Confirm New Password
        </label>
        <Input
          id="confirmPassword"
          v-model="form.confirmPassword"
          type="password"
          placeholder="Confirm your new password"
          :error="confirmPasswordError"
          :helper-text="confirmPasswordError"
          :disabled="isLoading"
          required
          class="w-full"
        />
      </div>

      <!-- Password Requirements -->
      <div class="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
        <h4 class="text-sm font-medium text-gray-900 dark:text-white mb-2">
          Password Requirements:
        </h4>
        <ul class="text-sm text-gray-600 dark:text-gray-300 space-y-1">
          <li class="flex items-center">
            <CheckIcon v-if="hasMinLength" class="w-4 h-4 text-green-500 mr-2" />
            <XMarkIcon v-else class="w-4 h-4 text-gray-400 dark:text-gray-500 mr-2" />
            At least 8 characters
          </li>
          <li class="flex items-center">
            <CheckIcon v-if="hasUppercase" class="w-4 h-4 text-green-500 mr-2" />
            <XMarkIcon v-else class="w-4 h-4 text-gray-400 dark:text-gray-500 mr-2" />
            At least one uppercase letter
          </li>
          <li class="flex items-center">
            <CheckIcon v-if="hasLowercase" class="w-4 h-4 text-green-500 mr-2" />
            <XMarkIcon v-else class="w-4 h-4 text-gray-400 dark:text-gray-500 mr-2" />
            At least one lowercase letter
          </li>
          <li class="flex items-center">
            <CheckIcon v-if="hasNumber" class="w-4 h-4 text-green-500 mr-2" />
            <XMarkIcon v-else class="w-4 h-4 text-gray-400 dark:text-gray-500 mr-2" />
            At least one number
          </li>
          <li class="flex items-center">
            <CheckIcon v-if="hasSpecialChar" class="w-4 h-4 text-green-500 mr-2" />
            <XMarkIcon v-else class="w-4 h-4 text-gray-400 dark:text-gray-500 mr-2" />
            At least one special character
          </li>
        </ul>
      </div>

      <!-- Submit Button -->
      <div class="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-600">
        <Button
          type="submit"
          :loading="isLoading"
          :disabled="!isFormValid || isLoading"
          class="w-full sm:w-auto"
        >
          <svg
            v-if="!isLoading"
            class="w-4 h-4 mr-2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-3a1 1 0 011-1h2.586l6.414-6.414a6 6 0 015.743-7.743z"
            />
          </svg>
          Change Password
        </Button>
      </div>
    </form>
  </Card>
</template>

<script setup lang="ts">
  import { ref, computed, watch } from 'vue';
  import { CheckIcon, XMarkIcon } from '@heroicons/vue/24/outline';
  import { Card, Button, Input, Alert } from '@asabdev/asab-ui';
  import { useUserProfile } from '../composables/useUserProfile';
  import { passwordChangeSchema, type PasswordChangeFormData } from '@/lib/validation/users';

  const { isLoading, hasError, error, updateSuccess, changePassword, clearMessages } =
    useUserProfile();

  // Form state
  const form = ref<PasswordChangeFormData>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const currentPasswordError = ref<string>('');
  const newPasswordError = ref<string>('');
  const confirmPasswordError = ref<string>('');

  // Password strength validation
  const hasMinLength = computed(() => form.value.newPassword.length >= 8);
  const hasUppercase = computed(() => /[A-Z]/.test(form.value.newPassword));
  const hasLowercase = computed(() => /[a-z]/.test(form.value.newPassword));
  const hasNumber = computed(() => /[0-9]/.test(form.value.newPassword));
  const hasSpecialChar = computed(() => /[^A-Za-z0-9]/.test(form.value.newPassword));

  const passwordStrength = computed(() => {
    let strength = 0;
    if (hasMinLength.value) strength++;
    if (hasUppercase.value) strength++;
    if (hasLowercase.value && hasNumber.value) strength++;
    if (hasSpecialChar.value) strength++;
    return strength;
  });

  const strengthColor = computed(() => {
    const colors = ['bg-red-500', 'bg-orange-500', 'bg-yellow-500', 'bg-green-500'];
    return colors[passwordStrength.value - 1] || 'bg-red-500';
  });

  const strengthTextColor = computed(() => {
    const colors = ['text-red-600', 'text-orange-600', 'text-yellow-600', 'text-green-600'];
    return colors[passwordStrength.value - 1] || 'text-red-600';
  });

  const strengthText = computed(() => {
    const texts = ['Weak', 'Fair', 'Good', 'Strong'];
    return texts[passwordStrength.value - 1] || 'Very Weak';
  });

  // Form validation
  const isFormValid = computed(() => {
    try {
      passwordChangeSchema.parse(form.value);
      return !currentPasswordError.value && !newPasswordError.value && !confirmPasswordError.value;
    } catch {
      return false;
    }
  });

  // Validation functions
  const validateForm = () => {
    try {
      passwordChangeSchema.parse(form.value);
      currentPasswordError.value = '';
      newPasswordError.value = '';
      confirmPasswordError.value = '';
    } catch (error: unknown) {
      const zodError = error as { errors?: Array<{ path: string[]; message: string }> };
      const errors = zodError.errors || [];

      currentPasswordError.value = '';
      newPasswordError.value = '';
      confirmPasswordError.value = '';

      errors.forEach(err => {
        if (err.path.includes('currentPassword')) {
          currentPasswordError.value = err.message;
        } else if (err.path.includes('newPassword')) {
          newPasswordError.value = err.message;
        } else if (err.path.includes('confirmPassword')) {
          confirmPasswordError.value = err.message;
        }
      });
    }
  };

  // Watch form for real-time validation
  watch(form, validateForm, { deep: true });

  // Form handlers
  const handleSubmit = async () => {
    try {
      validateForm();
      if (!isFormValid.value) return;

      await changePassword(form.value.currentPassword, form.value.newPassword);

      // Reset form on success
      if (updateSuccess.value) {
        form.value = {
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        };
      }
    } catch (error) {
      console.error('Password change failed:', error);
    }
  };
</script>
